import { isAfter, isBefore } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable } from 'mobx'
import { trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, formatDate, parseISODate } from '@avantstay/backoffice-core'

import { Home } from '@new/__global/components/HomeSelect/HomeSelect.types'
import { AlterBookingBasicParams } from '@new/domains/bookings/_types'
import { getFeeOverrideWithCCFee } from '@new/domains/bookings/ModifyBookingSidePanel/components/ModifyBookingForm/components/PricingFields/utils'
import {
  AlterBookingActions,
  AlterBookingInputs,
} from '@new/domains/bookings/ModifyBookingSidePanel/components/ModifyBookingForm/ModifyBookingForm.types'
import {
  DatePrices,
  FloorPrices,
} from '@new/domains/bookings/ModifyBookingSidePanel/ModifyBookingSidePanel.definitions'
import {
  BookingModificationRestrictions,
  BookingPeriod,
  GetNewBookingPreviewVariables,
  PreviewBookingAlteration,
  PreviewBookingAlterationNights,
  PreviewBookingAlterationTaxes,
  SetNewBookingPreviewValues,
} from '@new/domains/bookings/ModifyBookingSidePanel/ModifyBookingSidePanel.types'
import {
  filterDatesWithoutAvailability,
  getIsRoomTypeAlteration,
} from '@new/domains/bookings/ModifyBookingSidePanel/utils'
import { checkIsCreditCardFee } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/components/EditBookingPrice/EditBookingPrice.utils'
import { PropertyCalendarStayLimits } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { getBlockedDates } from '@new/domains/bookingsTapeChart/components/RowPeriod/utils'
import { AlterHotelBookingProps } from '@new/domains/hotelTapeChart/HotelTapeChart.types'
import {
  alterHotelBooking,
  previewHotelAlterationForModify,
  unassignRoomFromBooking,
} from '@new/domains/hotelTapeChart/stores/api/bookingsArriere'

import { arriere } from '@/services/arriere'
import { trackError, trackEvent } from '@/tracking/customEventReport'
import { PropertyRestrictions } from '@/types/servicesTypes/Properties.types'
import {
  alterBookingLockedPrice,
  bookingModificationPossibility,
  getBookingFolioInfo,
  previewAlteration,
} from '@arriere/modules/bookings'
import { getHomesListWithCalendar } from '@arriere/modules/properties'
import { HomesWithCalendarInput } from '@arriere/types/Properties.types'

import {
  BookingFolioSummaryItem,
  BookingFolioSummaryProps,
  ModifyBookingInfo,
  PropertyRestrictionsInfoProps,
} from './_types'
import { getModifyBookingInfo } from './api/modifyBookingArriere'

const GENERIC_ALTER_BOOKING_ERROR = "Couldn't save your changes. Please try again."

const getAlterBookingErrorMessage = (error: unknown): string => {
  const rawMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  const message = rawMessage.replace(/^Error:\s*/i, '').trim()
  return message || GENERIC_ALTER_BOOKING_ERROR
}

class _BookingModificationStore {
  //
  // Observables
  //

  // __ Booking Related Observables

  booking?: ModifyBookingInfo = undefined

  originalBookingFolio?: BookingFolioSummaryProps = undefined

  newBookingPeriod?: BookingPeriod = undefined

  bookingFloorPrices?: FloorPrices[] = undefined

  bookingModificationRestrictions?: BookingModificationRestrictions = undefined

  viewMode: 'roomType' | 'property' = 'property'

  // __ Home Related Observables

  selectedHomeAvailability?: DatePrices[] = undefined

  selectedHomeRestrictions?: PropertyRestrictions = undefined

  selectedHomeStayLimits?: PropertyCalendarStayLimits = undefined

  // __ Pricing Observables

  previewBookingAlteration?: PreviewBookingAlteration = undefined

  // This observable shows the quoted price without any modification from the user on the prices
  quotedPreviewBookingAlteration?: PreviewBookingAlteration = undefined

  alterBookingError?: string = undefined

  // __ Loading and cycle related Observables

  isLoadingBookingInfo = false

  isLoadingNewPrices = false

  isLoadingAlterBooking = false

  isLoadingHomeAvailability = false

  constructor() {
    makeObservable(this, {
      booking: observable,
      originalBookingFolio: observable,
      newBookingPeriod: observable,
      bookingFloorPrices: observable,
      bookingModificationRestrictions: observable,
      viewMode: observable,
      selectedHomeAvailability: observable,
      selectedHomeRestrictions: observable,
      selectedHomeStayLimits: observable,
      previewBookingAlteration: observable,
      quotedPreviewBookingAlteration: observable,
      alterBookingError: observable,
      isLoadingBookingInfo: observable,
      isLoadingNewPrices: observable,
      isLoadingAlterBooking: observable,
      isLoadingHomeAvailability: observable,
      originalBookingPeriod: computed,
      originalTaxItems: computed,
      quotedTaxItems: computed,
      originalFeeItems: computed,
      quotedFeeItems: computed,
      previewFeeItems: computed,
      previewCreditCardFeeItem: computed,
      previewNightItems: computed,
      isAlterationUnavailable: computed,
      previewTaxItems: computed,
      removedTaxItems: computed,
      removedFeeItems: computed,
      originalNightItems: computed,
      quotedNightItems: computed,
      removedNightItems: computed,
      selectedBookingPeriod: computed,
      hasModifiedOriginalPeriod: computed,
      originalBookingSummaryChanged: computed,
      isRoomTypeView: computed,
      reset: action.bound,
      setViewMode: action.bound,
      setSelectedBooking: action.bound,
      setOriginalBookingFolio: action.bound,
      setNewBookingPeriod: action.bound,
      setSelectedHomeAvailability: action.bound,
      setBookingModificationRestrictions: action.bound,
      setSelectedHomeRestrictions: action.bound,
      setSelectedHomeStayLimits: action.bound,
      setPreviewBookingAlteration: action.bound,
      setQuotedPreviewBookingAlteration: action.bound,
      setBookingFloorPrices: action.bound,
      setNewBookingPreviewValues: action.bound,
    })

    this.searchHomes = trackedAction<any>(this.searchHomes.bind(this))
    this.getBookingFloorPrices = trackedAction<any>(this.getBookingFloorPrices.bind(this))
    this.getBookingModificationPossibility = trackedAction<any>(
      this.getBookingModificationPossibility.bind(this),
    )
    this.getHomeStayLimits = trackedAction<any>(this.getHomeStayLimits.bind(this))
    this.getHomeRestrictionsInfo = trackedAction<any>(this.getHomeRestrictionsInfo.bind(this))
    this.alterBooking = trackedAction<any>(this.alterBooking.bind(this))
    this.alterBookingWithLockedPrice = trackedAction<any>(
      this.alterBookingWithLockedPrice.bind(this),
    )
    this.getHomeAvailability = trackedAction<any>(this.getHomeAvailability.bind(this))
    this.getNewBookingPreview = trackedAction<any>(this.getNewBookingPreview.bind(this))
    this.getBookingFolio = trackedAction<any>(this.getBookingFolio.bind(this))
  }

  //
  // Computed values
  //

  get originalBookingPeriod() {
    const { booking } = this
    if (booking)
      return {
        start: booking.detailedCheckIn.effectiveTime,
        end: booking.detailedCheckOut.effectiveTime,
      }
    return undefined
  }

  get originalTaxItems(): BookingFolioSummaryItem[] | undefined {
    return this.originalBookingFolio?.items.filter(({ kind }) => kind === 'TAX')
  }

  get quotedTaxItems(): PreviewBookingAlterationTaxes | undefined {
    return this.quotedPreviewBookingAlteration?.taxes
  }

  get originalFeeItems(): BookingFolioSummaryItem[] | undefined {
    return this.originalBookingFolio?.items.filter(({ kind }) => kind === 'FEE')
  }

  get quotedFeeItems() {
    return this.quotedPreviewBookingAlteration?.feesBreakdown.actual || []
  }

  get previewFeeItems() {
    return this.previewBookingAlteration?.feesBreakdown.actual || []
  }

  get previewCreditCardFeeItem() {
    return this.previewBookingAlteration?.feesBreakdown.actual?.find(fee =>
      checkIsCreditCardFee(fee.id),
    )
  }

  get previewNightItems(): PreviewBookingAlterationNights | undefined {
    return this.previewBookingAlteration?.nights
  }

  get isAlterationUnavailable(): boolean {
    return this.previewBookingAlteration?.availabilityDetails?.isAvailable === false
  }

  get previewTaxItems(): PreviewBookingAlterationTaxes | undefined {
    return this.previewBookingAlteration?.taxes
  }

  get removedTaxItems(): BookingFolioSummaryItem[] | undefined {
    const { originalTaxItems, quotedTaxItems } = this
    if (!quotedTaxItems || !originalTaxItems) return []
    return originalTaxItems.filter(
      originalItem =>
        !quotedTaxItems.find(
          quotedItem => quotedItem.id === originalItem.id || quotedItem.name === originalItem.title,
        ),
    )
  }

  get removedFeeItems(): BookingFolioSummaryItem[] | undefined {
    const { originalFeeItems, quotedFeeItems } = this
    if (!quotedFeeItems || !originalFeeItems) return []
    return originalFeeItems.filter(
      originalItem =>
        !quotedFeeItems.find(
          quotedItem => quotedItem.id === originalItem.id || quotedItem.name === originalItem.title,
        ),
    )
  }

  get originalNightItems(): BookingFolioSummaryItem[] | undefined {
    return this.originalBookingFolio?.items.filter(({ kind }) => kind === 'NIGHT')
  }

  get quotedNightItems(): { date: IDate; price: number }[] | undefined {
    return this.quotedPreviewBookingAlteration?.nights
  }

  get removedNightItems():
    | { beforePreview: BookingFolioSummaryItem[]; afterPreview: BookingFolioSummaryItem[] }
    | undefined {
    const { originalNightItems, quotedNightItems } = this
    if (!quotedNightItems || !originalNightItems) return undefined
    const firstPreviewNight = quotedNightItems[0].date
    const lastPreviewNight = quotedNightItems[quotedNightItems.length - 1].date
    const beforePreview = originalNightItems.filter(
      originalItem =>
        originalItem.date &&
        isBefore(parseISODate(originalItem.date), parseISODate(firstPreviewNight)) &&
        !quotedNightItems.find(quotedItem => quotedItem.date === originalItem.date),
    )
    const afterPreview = originalNightItems.filter(
      originalItem =>
        originalItem.date &&
        isAfter(parseISODate(originalItem.date), parseISODate(lastPreviewNight)) &&
        !quotedNightItems.find(quotedItem => quotedItem.date === originalItem.date),
    )
    return { beforePreview, afterPreview }
  }

  get selectedBookingPeriod(): BookingPeriod | undefined {
    const { booking, newBookingPeriod } = this
    if (newBookingPeriod) return newBookingPeriod
    if (booking)
      return {
        fromDate: booking.period.start,
        toDate: booking.period.end,
      }
    return undefined
  }

  get hasModifiedOriginalPeriod(): boolean {
    const { selectedBookingPeriod, booking } = this
    const hasModifiedStartDate =
      formatDate(booking?.period.start) !== formatDate(selectedBookingPeriod?.fromDate)
    const hasModifiedEndDate =
      formatDate(booking?.period.end) !== formatDate(selectedBookingPeriod?.toDate)
    return hasModifiedStartDate || hasModifiedEndDate
  }

  get originalBookingSummaryChanged(): boolean {
    const { previewBookingAlteration, originalBookingFolio } = this
    const totalChanged =
      originalBookingFolio?.summary.total !== previewBookingAlteration?.newSummary.total
    const valueNightsAndFeesChanged =
      originalBookingFolio?.summary.valueNightsAndFees !==
      previewBookingAlteration?.newSummary.valueNightsAndFees
    const itemsChanged = !isEqual(originalBookingFolio?.items, previewBookingAlteration?.items)
    return totalChanged || valueNightsAndFeesChanged || itemsChanged
  }

  getBlockedCalendarDates = computedFn(
    (selectedPeriod?: { fromDate?: string; toDate?: string }) => {
      const { selectedHomeAvailability, booking } = this
      if (
        !selectedHomeAvailability ||
        !selectedPeriod?.fromDate ||
        !selectedPeriod?.toDate ||
        !booking?.period
      ) {
        return undefined
      }

      return getBlockedDates({
        allDatePrices: selectedHomeAvailability,
        selectedPeriod: {
          start: selectedPeriod.fromDate,
          end: selectedPeriod.toDate,
        },
        originalPeriod: booking?.period,
      })
    },
  )

  // Room-type calendar blocking. Same night rule as a single unit (OCCUPIED and
  // CHECK-OUT-AND-CHECK-IN are blocked; a CHECK-IN day is blocked only as a start, never as
  // the check-out end), but applied only to dates the room type has no free unit for
  // (availabilityCount === 0). While any unit is free the date stays open.
  getRoomTypeBlockedCalendarDates = computedFn(
    (selectedPeriod?: { fromDate?: string; toDate?: string }) => {
      const { selectedHomeAvailability } = this
      const blockedByStatus = this.getBlockedCalendarDates(selectedPeriod)

      if (!blockedByStatus || !selectedHomeAvailability) return blockedByStatus

      return filterDatesWithoutAvailability(blockedByStatus, selectedHomeAvailability)
    },
  )

  //
  // Actions
  //

  get isRoomTypeView() {
    return this.viewMode === 'roomType'
  }

  setViewMode(mode: 'roomType' | 'property') {
    this.viewMode = mode
  }

  async reset() {
    this.booking = undefined
    this.viewMode = 'property'
    this.originalBookingFolio = undefined
    this.newBookingPeriod = undefined
    this.bookingFloorPrices = undefined
    this.bookingModificationRestrictions = undefined
    this.selectedHomeAvailability = undefined
    this.selectedHomeRestrictions = undefined
    this.selectedHomeStayLimits = undefined
    this.previewBookingAlteration = undefined
    this.quotedPreviewBookingAlteration = undefined
    this.alterBookingError = undefined
    this.isLoadingBookingInfo = false
    this.isLoadingNewPrices = false
    this.isLoadingAlterBooking = false
    this.isLoadingHomeAvailability = false
  }

  async setSelectedBooking(bookingId?: string) {
    if (bookingId) {
      this.isLoadingBookingInfo = true
      const [
        booking,
        originalBookingFolio,
        bookingModificationRestrictions,
        previewBookingAlteration,
      ] = await Promise.all([
        getModifyBookingInfo(bookingId),
        this.getBookingFolio(bookingId),
        this.getBookingModificationPossibility(bookingId),
        this.getNewBookingPreview({ bookingId }),
      ]).catch(error => {
        Alert.Error(`Error while loading booking info. Details: ${error}`)
        return []
      })

      const isLockPrice = this.previewBookingAlteration?.isLockPrice
      const updatedPreviewBookingAlteration = isLockPrice
        ? {
            ...(previewBookingAlteration ?? {}),
            isLockPrice,
          }
        : previewBookingAlteration

      this.booking = booking
      this.viewMode = getIsRoomTypeAlteration(booking) ? 'roomType' : 'property'
      this.originalBookingFolio = originalBookingFolio
      this.bookingModificationRestrictions = bookingModificationRestrictions
      this.previewBookingAlteration =
        updatedPreviewBookingAlteration as unknown as PreviewBookingAlteration
      this.quotedPreviewBookingAlteration = previewBookingAlteration
      this.isLoadingBookingInfo = false
    }
  }

  setOriginalBookingFolio(originalFolio?: BookingFolioSummaryProps) {
    this.originalBookingFolio = originalFolio
  }

  setNewBookingPeriod(period?: BookingPeriod) {
    this.newBookingPeriod = period
  }

  setSelectedHomeAvailability(selectedHomeAvailability?: DatePrices[]) {
    this.selectedHomeAvailability = selectedHomeAvailability
  }

  setBookingModificationRestrictions(
    bookingModificationRestrictions?: BookingModificationRestrictions,
  ) {
    this.bookingModificationRestrictions = bookingModificationRestrictions
  }

  setSelectedHomeRestrictions(selectedHomeRestrictions?: PropertyRestrictions) {
    this.selectedHomeRestrictions = selectedHomeRestrictions
  }

  setSelectedHomeStayLimits(selectedHomeStayLimits?: PropertyCalendarStayLimits) {
    this.selectedHomeStayLimits = selectedHomeStayLimits
  }

  setPreviewBookingAlteration(previewBookingAlteration?: PreviewBookingAlteration) {
    this.previewBookingAlteration = previewBookingAlteration
  }

  setQuotedPreviewBookingAlteration(previewBookingAlteration?: PreviewBookingAlteration) {
    this.quotedPreviewBookingAlteration = previewBookingAlteration
  }

  setBookingFloorPrices(bookingFloorPrices?: FloorPrices[]) {
    this.bookingFloorPrices = bookingFloorPrices
  }

  async setNewBookingPreviewValues({
    bookingPreviewVariables,
    lockPrice,
    onError,
    onSuccess,
  }: SetNewBookingPreviewValues) {
    const {
      nightPrices,
      feeOverride,
      bookingId,
      taxExemption,
      newRange,
      homeId,
      includeCCfee,
      hotelRoomTypeId,
    } = bookingPreviewVariables

    const feeOverrides = getFeeOverrideWithCCFee(
      feeOverride,
      this.previewCreditCardFeeItem,
      includeCCfee,
    )

    const newBookingPreviewVariables = {
      bookingId,
      nightPrices,
      feeOverrides: feeOverrides?.map(fee => ({
        value: fee.value,
        itemId: fee.itemId,
        feeId: fee.feeId!,
      })),
      taxExemption,
      newRange,
      newHomeId: homeId,
      hotelRoomTypeId,
      lockedPrice: lockPrice,
    }

    const newBookingPreview = await this.getNewBookingPreview(newBookingPreviewVariables)

    if (!newBookingPreview) {
      onError?.()
      return
    }

    let quotedBookingPreview = newBookingPreview

    if ((nightPrices || []).length > 0 || (feeOverride || []).length > 0 || taxExemption) {
      const newQuotedBookingPreview = await this.getNewBookingPreview({
        ...newBookingPreviewVariables,
        taxExemption: false,
        feeOverrides: [],
        nightPrices: [],
        lockedPrice: false,
        queryAlias: 'previewAlterationQuote',
      })
      if (!newQuotedBookingPreview) {
        onError?.()
        return
      }
      quotedBookingPreview = newQuotedBookingPreview
      this.setQuotedPreviewBookingAlteration(newQuotedBookingPreview)
    }

    if ((nightPrices || []).length === 0 && (feeOverride || []).length === 0 && !taxExemption) {
      this.setQuotedPreviewBookingAlteration(newBookingPreview)
    }

    const bookingPreviewAlteration =
      lockPrice || this.previewBookingAlteration?.isLockPrice
        ? {
            ...newBookingPreview,
            isLockPrice: lockPrice ?? this.previewBookingAlteration?.isLockPrice,
          }
        : newBookingPreview

    this.setPreviewBookingAlteration(bookingPreviewAlteration)
    onSuccess?.(newBookingPreview, quotedBookingPreview)
  }

  //
  // Tracked Actions
  //

  async getBookingFolio(bookingId: string): Promise<BookingFolioSummaryProps | undefined> {
    return getBookingFolioInfo(bookingId)
  }

  async getNewBookingPreview({
    bookingId,
    nightPrices = [],
    taxExemption,
    newRange,
    newHomeId,
    feeOverrides,
    hotelRoomTypeId,
    lockedPrice,
    queryAlias,
  }: GetNewBookingPreviewVariables): Promise<PreviewBookingAlteration | undefined> {
    const { booking, selectedBookingPeriod } = this
    if (!booking || !selectedBookingPeriod) return Promise.resolve(undefined)
    this.isLoadingNewPrices = true

    const feeOverridesMapped = feeOverrides?.map(item => ({ value: item.value, feeId: item.feeId }))
    const range = newRange
      ? { start: formatDate(newRange.fromDate), end: formatDate(newRange.toDate) }
      : undefined

    const resolvedNewHomeId = newHomeId || undefined

    const roomTypeId = this.isRoomTypeView
      ? (hotelRoomTypeId ?? booking.hotelRoomType?.id)
      : undefined

    const preview = roomTypeId
      ? previewHotelAlterationForModify({
          bookingId,
          hotelRoomTypeId: roomTypeId,
          newHomeId: resolvedNewHomeId,
          newRange: range,
          nightPrices,
          feeOverrides: feeOverridesMapped,
          taxExemption,
          lockedPrice,
          queryAlias,
        })
      : previewAlteration({
          bookingId,
          newHomeId: resolvedNewHomeId,
          newRange: range,
          taxExemption,
          feeOverrides: feeOverridesMapped,
          nightPrices,
          queryAlias,
        }).then(({ data, warnings, errors }) => {
          if (warnings?.length) {
            Alert.Info(
              `Something went wrong while calculating new prices. Details: ${warnings[0]?.message}`,
            )
          }

          if (errors?.length) {
            throw new Error(errors[0]?.message)
          }

          return data.previewAlteration
        })

    return preview
      .catch(error => {
        const errorMessage = `Something went wrong while calculating new prices. Details: ${error}`
        Alert.Error(errorMessage)
        return Promise.reject(new Error(errorMessage))
      })
      .finally(() => {
        this.isLoadingNewPrices = false
      })
  }

  async getHomeAvailability(
    homeId: string,
    period: BookingPeriod,
  ): Promise<DatePrices[] | undefined> {
    try {
      this.isLoadingHomeAvailability = true
      const homeAvailability = await arriere.properties.calendarDatePricesAvailability({
        homeId,
        from: period.fromDate,
        to: period.toDate,
      })
      return homeAvailability.sort((a, b) => (a.date < b.date ? -1 : 1))
    } catch (error) {
      Alert.Error(`Error while getting home availability. Details: ${error}`)
      return Promise.resolve(undefined)
    } finally {
      this.isLoadingHomeAvailability = false
    }
  }

  private processWarnings = (
    warnings: { message?: string }[],
    id: string,
    onWarning?: VoidFunction,
  ) => {
    this.alterBookingError = warnings[warnings.length - 1]?.message
    trackError('Booking Modification Warning', { id, warning: warnings[0].message })
    Alert.Error(
      `Something went wrong, please review your information and try again. Details: ${warnings[warnings.length - 1].message}`,
    )
    onWarning?.()
  }

  private processErrors = (errors: { message?: string }[], id: string) => {
    const errorMessage = errors?.flatMap(error => error?.message).join('\n')
    this.alterBookingError = errorMessage
    trackError('Booking Modification Error', { id, error: errorMessage })
    Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
    return false
  }

  private processSuccess = (id: string, onSuccess?: VoidFunction) => {
    const successMessage = 'Booking modified with success!'
    trackEvent(successMessage, { bookingId: id })
    Alert.Success(successMessage)
    onSuccess?.()
  }

  async alterBookingWithLockedPrice(params: AlterBookingBasicParams) {
    return await alterBookingLockedPrice(params)
  }

  async alterBooking({
    onSuccess,
    onWarning,
    ...variables
  }: AlterBookingInputs & AlterBookingActions) {
    const { bookingId, homeId, taxExemption, lockPrice, newRange } = variables
    try {
      if (!bookingId) throw new Error('Booking id was not provided')
      this.isLoadingAlterBooking = true
      this.alterBookingError = ''

      const newFees =
        variables.feeOverrides?.map(item => ({
          id: item.feeId,
          name: item.name,
          amount: item.value,
          itemId: item.itemId,
        })) || []

      const { errors, warnings, data } = lockPrice
        ? await this.alterBookingWithLockedPrice({ bookingId, homeId, taxExemption, newRange })
        : await arriere.bookings.alterBooking({
            ...variables,
            newFees,
          })

      if (warnings?.length) {
        this.processWarnings(warnings, bookingId, onWarning)
      }

      if (errors?.length) {
        this.processErrors(errors, bookingId)
      }

      if (data) {
        this.processSuccess(bookingId, onSuccess)
        return data
      } else {
        throw new Error('Booking modification failed')
      }
    } catch (error) {
      this.alterBookingError = getAlterBookingErrorMessage(error)
      return false
    } finally {
      this.isLoadingAlterBooking = false
    }
  }

  async alterHotelRoomBooking({
    onSuccess,
    ...variables
  }: AlterHotelBookingProps & Pick<AlterBookingActions, 'onSuccess'>) {
    const { bookingId } = variables
    try {
      if (!bookingId) throw new Error('Booking id was not provided')
      this.isLoadingAlterBooking = true
      this.alterBookingError = ''

      const { success } = await alterHotelBooking({
        ...variables,
        bookingSource: this.booking?.source,
      })

      if (success) {
        this.processSuccess(bookingId, onSuccess)
        return true
      }

      throw new Error('Booking modification failed')
    } catch (error) {
      this.alterBookingError = getAlterBookingErrorMessage(error)
      return false
    } finally {
      this.isLoadingAlterBooking = false
    }
  }

  async removeBookingRoomWithAlteration({
    bookingId,
    onSuccess,
    ...alteration
  }: AlterHotelBookingProps & Pick<AlterBookingActions, 'onSuccess'>) {
    try {
      if (!bookingId) throw new Error('Booking id was not provided')
      this.isLoadingAlterBooking = true
      this.alterBookingError = ''

      // Apply the date/price changes first, then unassign the room so both persist.
      const { success } = await alterHotelBooking({
        bookingId,
        ...alteration,
        bookingSource: this.booking?.source,
      })
      if (!success) throw new Error('Booking modification failed')

      const { completed, invalid } = await unassignRoomFromBooking(bookingId)
      if (completed) {
        this.processSuccess(bookingId, onSuccess)
        return true
      }

      const invalidReason = invalid?.find(item => item.bookingId === bookingId)?.reason
      throw new Error(invalidReason ?? 'Room removal failed')
    } catch (error) {
      this.alterBookingError = getAlterBookingErrorMessage(error)
      return false
    } finally {
      this.isLoadingAlterBooking = false
    }
  }

  getHomeRestrictionsInfo({
    homeId,
    period,
  }: PropertyRestrictionsInfoProps): Promise<PropertyRestrictions | undefined> {
    return arriere.properties.getPropertyRestrictionsById({ homeId, period })
  }

  async getHomeStayLimits(homeId: string, from: string, to: string) {
    if (!homeId) return Promise.resolve(undefined)
    return arriere.properties.getPropertyStayLimit(homeId, formatDate(from), formatDate(to))
  }

  async getBookingModificationPossibility(bookingId: string) {
    return bookingModificationPossibility(bookingId)
  }

  async getBookingFloorPrices({
    bookingId,
    newRange: { fromDate, toDate },
    homeId,
  }: {
    bookingId: string
    newRange: { fromDate: string; toDate: string }
    homeId?: string
  }) {
    return arriere.bookings.bookingFloorPrice(
      bookingId,
      {
        start: formatDate(fromDate),
        end: formatDate(toDate),
      },
      homeId,
    )
  }

  async searchHomes({
    from,
    to,
    name,
    hotelId,
    hotelRoomTypeId,
    currentHomeId,
  }: Partial<HomesWithCalendarInput>): Promise<Home[]> {
    if (from && to) {
      return getHomesListWithCalendar({ from, to, name, hotelId, hotelRoomTypeId })
        .then(response => {
          if (response?.data?.results) {
            const homes = response.data.results
              .filter(homeItem => homeItem.id !== currentHomeId)
              ?.map(home => ({
                ...home,
                bookings: home.bookings.results,
              }))

            const homeIds = homes.map(home => home.id)

            if (homeIds) {
              return arriere.properties
                .propertyBlockedPeriods({
                  homeIds,
                  from: formatDate(from),
                  to: formatDate(to),
                })
                .then(blockedPeriods => {
                  const homeWithBlockedPeriods = homes.map(home => ({
                    ...home,
                    blockedPeriods: blockedPeriods.filter(block => block.homeId === home.id),
                  }))
                  return homeWithBlockedPeriods
                })
                .catch(error => {
                  Alert.Error(`Error while searching for available properties. Details: ${error}`)
                  return []
                })
            }
            return homes
          }
          return Promise.resolve([])
        })
        .catch(error => {
          Alert.Error(`Error while searching for available properties. Details: ${error}`)
          return Promise.resolve([])
        })
    }
    return []
  }
}

export const BookingModificationStore = new _BookingModificationStore()
